import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { ArrowDown } from "components/icons/ArrowDown";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { SmallSparkle } from "components/icons/SmallSparkle";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";

type MobileSelectionActionsProps = {
  isExcludedView: boolean;
  isAllTxnSelected: boolean;
  isStripe: boolean;
  hasWritePermission: boolean;
  canRequestInfo: boolean;
  hasAISuggestion: boolean;
  isBusy: boolean;
  onCategorise: () => void;
  onUncategorise: () => void;
  onConfirmCategory: () => void;
  onSmartReview: () => void;
  onSendToChat: () => void;
  onRequestInfo: () => void;
  onClassification: () => void;
  onApplyAIClassifications: () => void;
  onUpgrade: () => void;
  onAssignVendor: () => void;
  onExclude: () => void;
  onRestore: () => void;
};

// On phones, the bulk actions live in one menu so the selection bar fits on screen.
export const MobileSelectionActions = ({
  isExcludedView,
  isAllTxnSelected,
  isStripe,
  hasWritePermission,
  canRequestInfo,
  hasAISuggestion,
  isBusy,
  onCategorise,
  onUncategorise,
  onConfirmCategory,
  onSmartReview,
  onSendToChat,
  onRequestInfo,
  onClassification,
  onApplyAIClassifications,
  onUpgrade,
  onAssignVendor,
  onExclude,
  onRestore,
}: MobileSelectionActionsProps) => {
  const isCategorisationDisabled = isStripe || !hasWritePermission;
  const isWriteDisabled = !hasWritePermission || isBusy;

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger asChild className="t-group">
        <span>
          <Button size="small">
            Actions
            <span className="group-data-state-open:t-rotate-180 t-transition-transform">
              <ArrowDown />
            </span>
          </Button>
        </span>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" side="top" sideOffset={8}>
          {isExcludedView && (
            <Dropdown.Item disabled={isWriteDisabled} onSelect={onRestore}>
              Restore
            </Dropdown.Item>
          )}

          {!isExcludedView && (
            <>
              <Dropdown.Item
                disabled={isCategorisationDisabled}
                onSelect={onCategorise}
              >
                Categorise
              </Dropdown.Item>
              <Dropdown.Item
                disabled={isCategorisationDisabled}
                onSelect={onUncategorise}
              >
                Uncategorise
              </Dropdown.Item>
              <Dropdown.Item
                disabled={isCategorisationDisabled || isBusy}
                onSelect={onConfirmCategory}
              >
                Confirm category
              </Dropdown.Item>
              <Dropdown.Item
                disabled={isCategorisationDisabled}
                onSelect={onSmartReview}
                className="t-flex t-items-center t-gap-2"
              >
                <span>Smart review</span>
                <Tag tagType="blue" size="small" rounded icon={false}>
                  New
                </Tag>
              </Dropdown.Item>

              {!isAllTxnSelected && (
                <>
                  <Dropdown.Separator />
                  <Dropdown.Item onSelect={onSendToChat}>
                    Send to chat
                  </Dropdown.Item>
                  {canRequestInfo && (
                    <Dropdown.Item onSelect={onRequestInfo}>
                      Request info
                    </Dropdown.Item>
                  )}

                  <Dropdown.Separator />
                  <PermissionBasedUI
                    feature={CLASSIFICATION_FEATURE_ACCESS}
                    onUpgrade={onUpgrade}
                    blockedUI={
                      <Dropdown.Item
                        onSelect={onUpgrade}
                        className="t-flex t-items-center t-gap-2"
                      >
                        <span>Classification</span>
                        <DiamondPurple height={13} width={14} />
                      </Dropdown.Item>
                    }
                  >
                    <Dropdown.Item
                      disabled={!hasWritePermission}
                      onSelect={onClassification}
                    >
                      Apply classification
                    </Dropdown.Item>
                    {hasAISuggestion && (
                      <Dropdown.Item
                        disabled={isWriteDisabled}
                        onSelect={onApplyAIClassifications}
                        className="t-flex t-items-center t-gap-2"
                      >
                        <span className="t-flex t-items-center t-text-purple">
                          <SmallSparkle width={16} height={16} />
                        </span>
                        <span>Apply AI suggested classes</span>
                      </Dropdown.Item>
                    )}
                  </PermissionBasedUI>
                </>
              )}

              <Dropdown.Separator />
              <Dropdown.Item
                disabled={!hasWritePermission}
                onSelect={onAssignVendor}
              >
                Assign vendor
              </Dropdown.Item>
              <Dropdown.Item disabled={isWriteDisabled} onSelect={onExclude}>
                Exclude
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
